import React, { useEffect, useState } from 'react';
import moment from "moment"
import { useSelector } from "react-redux";
import { GridCol, Grid } from 'pageFramework/uiControls/layouts/grid';
import { createCalendarScheduleService } from 'services';
import { useApiService, useDataApiState } from 'hooks';
import { ScheduleAppointmentSummary, SummaryAppointmentInfo, SummaryReferrerInfo, SummarySchedulerInfo, PatientInfo } from 'types';
import { ContentSpacer } from 'pageFramework/uiControls/layouts';
import { Accordion } from 'pageFramework/containers';
import { ReadOnlyField } from 'pageFramework/uiControls/inputs';
import ErrorSummary from 'components/common/errorSummary';
import LoadingIndicator from 'components/loadingIndicator';
import { Modal } from 'pageFramework/modals/modal';
import { Button } from 'pageFramework/uiControls/buttons';
import { ActionBar } from 'pageFramework/uiControls/layouts';

interface AppointmentSectionProps {
	apptInfo: SummaryAppointmentInfo;
	showInsuranceDetails: boolean;
	showReasonForVisit: boolean;
}

const AppointmentSection: React.FC<React.PropsWithChildren<AppointmentSectionProps>> = ({ apptInfo, showInsuranceDetails, showReasonForVisit }) => {
	return (
		<Grid numberOfColumns={1}>
			<GridCol>
				<ReadOnlyField label="Appointment Date and Time">{`${apptInfo.dateString} at ${apptInfo.providerTime}`}</ReadOnlyField>
			</GridCol>
			<GridCol>
				<Grid numberOfColumns={3}>
					<GridCol>
						<ReadOnlyField label="Visit ID">{apptInfo.visitId}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Provider Time">{apptInfo.providerTime}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Patient Time">{apptInfo.patientTime}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
			<GridCol>
				<ReadOnlyField label="Appointment Type">{apptInfo.appointmentType}</ReadOnlyField>
			</GridCol>
			<GridCol>
				<ReadOnlyField label="Mapped Appointment Type">{apptInfo.mappedAppointmentType}</ReadOnlyField>
			</GridCol>
			{showInsuranceDetails &&
				<GridCol>
					<Grid numberOfColumns={2}>
						<GridCol>
							<ReadOnlyField label="Payor Type">{apptInfo.payorType}</ReadOnlyField>
						</GridCol>
						<GridCol>
							<ReadOnlyField label="Insurance Provider">{apptInfo.insuranceProvider}</ReadOnlyField>
						</GridCol>
					</Grid>
				</GridCol>
			}
			{showReasonForVisit &&
				<GridCol>
					<ReadOnlyField label="Reason For Visit">{apptInfo.reasonForVisit}</ReadOnlyField>
				</GridCol>
			}
		</Grid>
	);
};

interface ReferredToSectionProps {
	refInfo: SummaryReferrerInfo;
}

const ReferredToSection: React.FC<React.PropsWithChildren<ReferredToSectionProps>> = ({ refInfo }) => {
	return (
		<Grid numberOfColumns={1}>
			<GridCol>
				<Grid numberOfColumns={2}>
					<GridCol>
						<ReadOnlyField label="Provider">{refInfo.providerName}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Designation">{refInfo.providerDesignation}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
			<GridCol>
				<Grid numberOfColumns={2}>
					<GridCol>
						<ReadOnlyField label="Specialty">{refInfo.providerSpecialty}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Required Language">{refInfo.requiredLanguage}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
			<GridCol>
				<Grid numberOfColumns={2}>
					<GridCol>
						<ReadOnlyField label="Office">{refInfo.providerOffice}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Phone">{refInfo.providerPhone}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
			<GridCol>
				<ReadOnlyField label="Address">{refInfo.providerAddress}</ReadOnlyField>
			</GridCol>
			<GridCol>
				<ReadOnlyField label="Additional Notes">{refInfo.notes}</ReadOnlyField>
			</GridCol>
		</Grid>
	);
};

interface ScheduledBySectionProps {
	scheduledByInfo: SummarySchedulerInfo;
}

const ScheduledBySection: React.FC<React.PropsWithChildren<ScheduledBySectionProps>> = ({ scheduledByInfo }) => {
	return (
		<Grid numberOfColumns={1}>
			<GridCol>
				<Grid numberOfColumns={2}>
					<GridCol>
						<ReadOnlyField label="Scheduled By">{scheduledByInfo.scheduledBy}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Timestamp">{scheduledByInfo.timestamp}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
			<GridCol>
				<Grid numberOfColumns={2}>
					<GridCol>
						<ReadOnlyField label="Referrer System">{scheduledByInfo.referrerSystem}</ReadOnlyField>
					</GridCol>
					<GridCol>
						<ReadOnlyField label="Referrer">{scheduledByInfo.referrer}</ReadOnlyField>
					</GridCol>
				</Grid>
			</GridCol>
		</Grid>
	);
};

interface InfoSectionProps {
	infoForCustomer: string;
	additionalInfoForCustomer: string;
}

const InfoSection: React.FC<React.PropsWithChildren<InfoSectionProps>> = ({ infoForCustomer, additionalInfoForCustomer }) => {
	return (
		<Grid numberOfColumns={1}>
			<GridCol>
				<ReadOnlyField label="Information For Customer">{infoForCustomer}</ReadOnlyField>
			</GridCol>
			<GridCol>
				<ReadOnlyField label="Additional Information">{additionalInfoForCustomer}</ReadOnlyField>
			</GridCol>
		</Grid>
	);
};

interface PatientSectionProps {
	patientInfo: PatientInfo;
	patientConfig: any;
    memberId: string;
}

const PatientSection: React.FC<React.PropsWithChildren<PatientSectionProps>> = ({ patientInfo, patientConfig, memberId }) => {
	return (
		<Grid numberOfColumns={1}>
			<GridCol>
				<Grid numberOfColumns={3}>
					{patientConfig.memberId.isVisible &&
						<GridCol>
							<ReadOnlyField label="Member ID">{memberId}</ReadOnlyField>
						</GridCol>
					}
					{patientConfig.homePhone.isVisible &&
						<GridCol>
							<ReadOnlyField label="Home Phone">{patientInfo.homePhone}</ReadOnlyField>
						</GridCol>
					}
					{patientConfig.mobilePhone.isVisible &&
						<GridCol>
							<ReadOnlyField label="Mobile Phone">{patientInfo.mobilePhone}</ReadOnlyField>
						</GridCol>
					}
					{patientConfig.gender.isVisible &&
						<GridCol>
							<ReadOnlyField label="Gender">{patientInfo.gender}</ReadOnlyField>
						</GridCol>
					}
					{patientConfig.dateOfBirth.isVisible &&
						<GridCol>
							<ReadOnlyField label="Date of Birth">{moment.utc(patientInfo.dateOfBirth).format("MM/DD/YYYY")}</ReadOnlyField>
						</GridCol>
					}
					{patientConfig.email.isVisible &&
						<GridCol>
							<ReadOnlyField label="Email">{patientInfo.email}</ReadOnlyField>
						</GridCol>
					}
				</Grid>
			</GridCol>
			{patientConfig.addressLine1.isVisible &&
				<GridCol>
					<ReadOnlyField label="Address">{patientInfo.address}</ReadOnlyField>
				</GridCol>
			}
		</Grid>
	);
};

interface AppointmentDetailsModalProps {
	show: boolean;
	onClose: () => void;
	refGuid: string;
}

interface AppointmentDetailsSections {
	[key: string]: any;
	appointment: boolean;
	referredTo: boolean;
	scheduled: boolean;
	info: boolean;
	patient: boolean;
}

export const AppointmentDetailsModal: React.FC<React.PropsWithChildren<AppointmentDetailsModalProps>> = (props) => {
	const { show, onClose, refGuid } = props;

	const [appointmentSummary, setAppointmentSummary] = useDataApiState<ScheduleAppointmentSummary>();
	const [openSections, setOpenSections] = useState<AppointmentDetailsSections>({
		appointment: false,
		referredTo: false,
		scheduled: false,
		info: false,
		patient: false,
	});

	const showInsuranceDetails = useSelector((state: any) => state.config.scheduling.showInsuranceOnAppointmentDetails);
	const showReasonForVisit = useSelector((state: any) => state.config.scheduling.showReasonForVisitApptDetails);
	const patientConfig = useSelector((state: any) => state.config.patient);

	const calendarScheduleService = useApiService(createCalendarScheduleService);

	const handleToggle = (section: string) => {
		setOpenSections((p: AppointmentDetailsSections) => {
			return { ...p, [section]: !p[section] };
		});
	};

	const handleToggleAll = (isOpen: boolean) => {
		setOpenSections({
			appointment: isOpen,
			referredTo: isOpen,
			scheduled: isOpen,
			info: isOpen,
			patient: isOpen,
		});
	};

	useEffect(() => {
		if (refGuid) calendarScheduleService.getAppointmentSummary(refGuid, setAppointmentSummary);
	}, [refGuid, setAppointmentSummary]);

	return (
		<Modal show={show} title="Appointment Details" onClose={onClose} showCloseButton={true} size="large">
			{appointmentSummary.loading && <LoadingIndicator />}
			{appointmentSummary.error && <ErrorSummary error={appointmentSummary.error} />}
			{appointmentSummary.data && !appointmentSummary.loading && (
				<>
					<ContentSpacer spacingFactor={1}>
						<ActionBar justify="end">
							<Button variant="white" onClick={() => handleToggleAll(true)}>
								Expand All
							</Button>
							<Button variant="white" onClick={() => handleToggleAll(false)}>
								Collapse All
							</Button>
						</ActionBar>
						<Accordion
							isOpen={openSections.patient}
							onToggle={() => handleToggle('patient')}
							header={`Patient: ${appointmentSummary.data?.patientInfo?.firstName} ${appointmentSummary.data?.patientInfo?.lastName}`}
						>
							<PatientSection patientInfo={appointmentSummary.data.patientInfo} memberId={appointmentSummary.data.memberId} patientConfig={patientConfig} />
						</Accordion>
					</ContentSpacer>
					<Accordion
						isOpen={openSections.appointment}
						onToggle={() => handleToggle('appointment')}
						header={`Appointment: ${appointmentSummary.data?.appointment?.dateString} at ${appointmentSummary.data?.appointment?.providerTime}`}
					>
						<AppointmentSection apptInfo={appointmentSummary.data.appointment} showInsuranceDetails={showInsuranceDetails} showReasonForVisit={showReasonForVisit} />
					</Accordion>
					<Accordion
						isOpen={openSections.referredTo}
						onToggle={() => handleToggle('referredTo')}
						header={`Referred To: ${appointmentSummary.data?.referrerInfo?.providerName}, ${appointmentSummary.data?.referrerInfo?.providerOffice}`}
					>
						<ReferredToSection refInfo={appointmentSummary.data.referrerInfo} />
					</Accordion>
					<Accordion
						isOpen={openSections.scheduled}
						onToggle={() => handleToggle('scheduled')}
						header={`Scheduled by: ${appointmentSummary.data?.schedulerInfo?.scheduledBy}`}
					>
						<ScheduledBySection scheduledByInfo={appointmentSummary.data?.schedulerInfo} />
					</Accordion>
					<Accordion isOpen={openSections.info} onToggle={() => handleToggle('info')} header="Information for Customer">
						<InfoSection
							infoForCustomer={appointmentSummary.data?.infoForCustomer}
							additionalInfoForCustomer={appointmentSummary.data?.additionalInfoForCustomer}
						/>
					</Accordion>
				</>
			)}
		</Modal>
	);
};

export default AppointmentDetailsModal;
